import type { MiddlewareHandler } from "hono";
import { validateApiKey } from "../../auth";
import { parseCapabilities } from "../../db/repositories/devices.repository";
import {
  authenticateContext,
  contextRegistry,
  E2EE_WS_SUBPROTOCOL,
  parseSubprotocols,
  refuseUnsealedIfPinned,
  TICKET_HEADER,
  TICKET_SUBPROTOCOL_PREFIX,
} from "../../e2ee/context";
import { E2EE_DEVICE_REVOKED } from "../../e2ee/protocol";
import { getLogger } from "../../logger";
import {
  hasCapability,
  legacyPrincipal,
  type Principal,
  requiredCapability,
} from "../../services/security/capabilities";
import type { AppEnv } from "../app";
import type { ApiDeps } from "../types/api-deps";

const log = getLogger("e2ee");

/** The one path a WebSocket ticket authenticates. */
const WS_PATH = "/ws";

function isLocalRequest(remoteAddr: string | undefined): boolean {
  const addr = remoteAddr ?? "";
  return addr === "127.0.0.1" || addr === "::1" || addr === "::ffff:127.0.0.1";
}

// `/healthz` is open to LOCAL callers only — the menubar poll, the deploy
// healthcheck and the updater's restart probe all hit
// `http://127.0.0.1:8766/healthz` with no credential and must keep working.
// `remoteAddress` cannot separate those from an external probe: behind the
// Cloudflare tunnel every request arrives from 127.0.0.1 and the streamer reads
// no forwarded-IP header (see `e2ee.routes.ts` §8). But cloudflared injects
// `Cf-Connecting-Ip` on every request it proxies, and a genuine loopback caller
// sets none — so the header's PRESENCE is the "came through the tunnel" signal.
// A tunneled `/healthz` therefore falls through to the same key/e2ee gate as
// every other route (encrypted context, Bearer, or `?key=`), while a local one
// stays open. An external probe cannot strip the header (cloudflared adds it at
// its own edge), and a local process forging it only denies its own `/healthz`
// — never a bypass.
const HEALTHZ_PATH = "/healthz";
const CF_TUNNEL_HEADER = "cf-connecting-ip";
// Localhost-only unauthenticated paths (menubar logs viewer).
const LOCAL_ONLY_PATHS = new Set(["/api/logs", "/api/logs/meta"]);
// /api/__update uses HMAC signature auth instead of Bearer; skip the
// Bearer-token middleware so the route handler can validate the signature.
// /api/e2ee/open is public for the same reason /api/pair/exchange is: the
// handshake IS the authentication, and the credential the middleware would look
// for only exists inside it (design.md §3.5, NONCE-DESIGN §10).
const PUBLIC_POST_PATHS = new Set(["/api/pair/exchange", "/api/__update", "/api/e2ee/open"]);
// /internal/sessions/:sessionId/progress also uses HMAC (Progress webhook),
// and the sessionId is dynamic so we match by prefix.
const PUBLIC_POST_PREFIXES = ["/internal/sessions/"];

export const authMiddleware =
  (deps: Pick<ApiDeps, "apiKey" | "localNoAuth" | "devicesRepo">): MiddlewareHandler<AppEnv> =>
  async (c, next) => {
    const path = new URL(c.req.url).pathname;
    const method = c.req.method;
    const isPublicPostPath =
      method === "POST" &&
      (PUBLIC_POST_PATHS.has(path) || PUBLIC_POST_PREFIXES.some((p) => path.startsWith(p)));
    // A local `/healthz` (no cloudflared header) is open; a tunneled one falls
    // through to the gate below, where an e2ee context, a Bearer, or a `?key=`
    // authenticates it and a bare probe gets 401.
    const isLocalHealthz = path === HEALTHZ_PATH && c.req.header(CF_TUNNEL_HEADER) === undefined;
    if (isLocalHealthz || isPublicPostPath) {
      await next();
      return;
    }

    const remoteAddr = c.env.incoming?.socket?.remoteAddress;
    if (LOCAL_ONLY_PATHS.has(path) && isLocalRequest(remoteAddr)) {
      await next();
      return;
    }

    // `--local-no-auth` gives any loopback caller the owner's authority. It used
    // to grant that by returning next() right here — before the capability check
    // below and before c.set("principal") — so the capability layer saw no
    // principal at all, and the WebSocket guard took its null-principal path
    // instead of an authorization decision. Resolving the caller to the owner
    // principal leaves the access identical and puts it through the same checks
    // as every other caller.
    const loopbackOwner = deps.localNoAuth && isLocalRequest(remoteAddr);

    const authorization = c.req.header("authorization");
    const bearer = authorization?.startsWith("Bearer ") ? authorization.slice(7) : undefined;
    const queryKey = c.req.query("key") ?? undefined;
    const presented = bearer ?? queryKey;

    // ─── A ticketed WebSocket upgrade authenticates by its ticket ───────
    //
    // NONCE-DESIGN §13: **no `Authorization` travels on a ticketed upgrade.**
    // The ticket came out of a Noise handshake against the device's own static
    // key and resolves to a context that names the device, so the long-term
    // credential has nothing left to prove — and sending it anyway would put a
    // device token on the wire on every reconnect, which is the exact leak the
    // sealed-credential ordering exists to close.
    //
    // **The consume is a synchronous map delete with no `await` in front of
    // it** (§14). Nothing above this line awaits, and `consumeTicket` reads and
    // deletes in one uninterrupted turn — so two concurrent upgrades presenting
    // one ticket cannot both see it, whatever order Node runs them in.
    //
    // A ticket that does not resolve — spent, expired, or lost to a restart —
    // is terminal for THIS upgrade. Falling through to a bearer or `?key=`
    // would turn a failed sealed attempt into a legacy plaintext socket. The
    // client recovers by opening a new context and ticket.
    // Mirror @hono/node-ws's upgrade predicate exactly: looser spends a ticket
    // without a socket; stricter can downgrade an upgrade to legacy auth.
    const isWsUpgrade =
      method === "GET" &&
      path === WS_PATH &&
      c.req.header("upgrade")?.toLowerCase() === "websocket";
    //
    // A browser presents the same ticket as a `tb-ticket.<ticket>` subprotocol
    // offer (see `E2EE_WS_SUBPROTOCOL`). Everything above holds for it
    // unchanged: the parse below is synchronous, and the consume is the same
    // call. Offering `threadbase-e2ee-v1` at all declares a sealed attempt, so
    // an offer with no ticket is refused rather than served as legacy — that is
    // also what makes the static protocol selection in `mountWebSocket` honest:
    // the protocol can only be selected on an upgrade a ticket authenticated.
    const offered = isWsUpgrade ? parseSubprotocols(c.req.header("sec-websocket-protocol")) : [];
    if (offered === null) return c.json({ error: "Bad Request" }, 400);
    const presentedTickets = offered
      .filter((p) => p.startsWith(TICKET_SUBPROTOCOL_PREFIX))
      .map((p) => p.slice(TICKET_SUBPROTOCOL_PREFIX.length));
    const headerTicket = isWsUpgrade ? c.req.header(TICKET_HEADER) : undefined;
    if (headerTicket !== undefined) presentedTickets.push(headerTicket);
    if (presentedTickets.length > 1) {
      // Two tickets — header plus subprotocol, or two offers — is refused, not
      // resolved by preferring one: the same rule as a ticket beside a
      // credential naming another device, since "which one authenticates"
      // should never be a precedence a client can be unsure of. Every presented
      // ticket is still spent, and a context this orphans is destroyed, exactly
      // as the capability refusal below does: only a caller holding a ticket
      // can resolve one, so the destroy is never someone else's to trigger.
      const registry = contextRegistry();
      for (const presented of presentedTickets) {
        const spent = registry.consumeTicket(presented);
        if (spent) registry.destroy(spent);
      }
      return c.json({ error: "Unauthorized" }, 401);
    }
    const ticket = presentedTickets[0] ?? (offered.includes(E2EE_WS_SUBPROTOCOL) ? "" : undefined);
    if (ticket !== undefined) {
      const registry = contextRegistry();
      const ctxId = registry.consumeTicket(ticket);
      const context = ctxId ? registry.get(ctxId) : null;
      if (!context) return c.json({ error: "Unauthorized" }, 401);
      // The shared tail, in `context.ts` because the REST unseal middleware
      // runs exactly the same decision off `X-TB-Ctx`: device row, per-request
      // `revoked_at`, credential-must-name-the-same-device, principal built
      // from the CONTEXT. The helper returns a pure verdict; each caller maps
      // the reason to its own response and applies the permitted lifecycle
      // effect.
      const auth = authenticateContext({
        context,
        devicesRepo: deps.devicesRepo(),
        presented,
      });
      if (!auth.ok) {
        // **Mapping the verdict to an answer is the CALLER's job**, which is
        // why the helper returns a `reason` and no status: the REST
        // middleware maps the same three onto HTTP, this maps them onto an
        // upgrade refusal, and neither imposes its policy on the other.
        //
        // **Destroy only on the unforgeable trigger.** `revoked_at` (and a
        // missing row) is a fact in our own database; a mismatched credential
        // is a header an attacker chose, and destroying on it turns the
        // safeguard into the weapon — on the REST channel `ctxId` travels in
        // a plaintext header, so anyone on path could forge a credential
        // beside an observed id and kill that device's context on repeat.
        //
        // Destroying here on a mismatch would in fact be harmless, since this
        // ticket is already spent and there is no second attempt to deny. It
        // is still not done, for SYMMETRY with the REST caller: a helper
        // whose two consumers apply different effects to one verdict is how
        // the dangerous behaviour gets copied from the harmless one later.
        if (auth.reason === "device-revoked") registry.destroy(context.ctxId);
        log.warn("[e2ee] upgrade refused by context authentication", {
          event: "e2ee.upgrade_refused",
          reason: auth.reason,
        });
        if (auth.reason === "device-revoked") {
          return c.json({ error: "This device is not paired", code: E2EE_DEVICE_REVOKED }, 403);
        }
        if (auth.reason === "no-device-store") {
          // Transient, and deliberately NOT `E2EE_DEVICE_REVOKED`: §9 defines
          // that code as a hard failure the client must never retry, and a
          // registry that could not be read says nothing about the pairing.
          // Same shape `/api/devices` already uses when its repo is missing.
          return c.json(
            { error: "Device registry is unavailable", code: "STORE_UNAVAILABLE" },
            503,
          );
        }
        return c.json({ error: "Unauthorized" }, 401);
      }

      c.set("e2eeContext", context);
      // A ticket AUTHENTICATES; it does not authorize. The capability check is
      // the same one every other caller passes.
      const required = requiredCapability(path, method);
      if (required !== null && !hasCapability(auth.principal, required)) {
        // **Destroy the context this refusal orphans.** `consumeTicket`
        // already spent the ticket AND called `markUsed`, which promotes the
        // context out of provisional and moves its deadline from the 30 s
        // ticket TTL to the full 24 h lifetime. Refusing without destroying
        // therefore leaves a context nobody can ever attach to — the ticket is
        // gone — sitting for a day and counting against the device's cap,
        // which is the opposite of what promoting it was for.
        //
        // Safe on this channel: the trigger is our own capability table, not
        // a header an attacker chose, and the ticket is already spent so
        // there is no second attempt to deny.
        registry.destroy(context.ctxId);
        return c.json({ error: "Forbidden", code: "MISSING_CAPABILITY", required }, 403);
      }
      try {
        deps.devicesRepo()?.touch(auth.principal.deviceId);
      } catch {
        // A liveness stamp must never deny a valid upgrade.
      }
      c.set("principal", auth.principal);
      await next();
      return;
    }

    // ─── A sealed REST request already resolved its own principal ──────
    //
    // `e2eeEnvelopeMiddleware` runs ahead of this one (D-9: the unseal sits in
    // front of authentication so the credential travels sealed) and, on a
    // request that unsealed under `X-TB-Ctx`, has already set the principal
    // from the CONTEXT's device row after re-checking `revoked_at`. Resolving
    // it again here would be worse than redundant: a sealed request carries no
    // `Authorization` at all (§13(b)), so the resolution below would find no
    // credential and 401 a request that authenticated correctly.
    //
    // **What is skipped is credential RESOLUTION, and nothing else.** The
    // capability check still runs, on the context's principal, exactly as it
    // does for every other caller. Read this as "skip the rest of the
    // middleware" and a read-only device gains full write authority the moment
    // it seals — `c.set("principal", …)` below happens AFTER the capability
    // gate, and not at all on the `required === null` early return, so a
    // principal being present says nothing about authority having been checked.
    //
    // The 403 this can produce is sealed on the way out by the envelope
    // middleware's response path, like every other answer that request is owed.
    // A few focused middleware harnesses provide only the pre-existing
    // Context surface; production Hono contexts always provide `get`.
    const contextPrincipal = c.get?.("principal");
    if (contextPrincipal) {
      const requiredForContext = requiredCapability(path, method);
      if (requiredForContext !== null && !hasCapability(contextPrincipal, requiredForContext)) {
        return c.json(
          { error: "Forbidden", code: "MISSING_CAPABILITY", required: requiredForContext },
          403,
        );
      }
      await next();
      return;
    }

    if (!presented && !loopbackOwner) {
      return c.json({ error: "Unauthorized" }, 401);
    }

    // Resolve the caller to a principal (C5).
    //
    // Device tokens are tried FIRST so a device credential is never mistaken
    // for the shared key, then the shared key falls back to a `legacy`
    // principal holding the full preset. Keeping the shared key working is what
    // lets this ship without breaking every already-paired device.
    let principal: Principal | null = null;

    const device = presented ? (deps.devicesRepo()?.authenticate(presented) ?? null) : null;
    if (device) {
      principal = {
        kind: "device",
        deviceId: device.device_id,
        capabilities: parseCapabilities(device.capabilities),
      };
      // Best-effort liveness stamp; a failure here must not deny a valid request.
      try {
        deps.devicesRepo()?.touch(device.device_id);
      } catch {
        // ignore
      }
    } else if (presented && validateApiKey(presented, deps.apiKey)) {
      principal = legacyPrincipal();
    } else if (loopbackOwner) {
      // No credential needed from loopback under the flag — and a wrong one does
      // not demote it either, which is what the old unconditional bypass did.
      principal = legacyPrincipal();
    }

    if (!principal) {
      return c.json({ error: "Unauthorized" }, 401);
    }

    // ─── Downgrade enforcement: 426, never 401 (design.md §6.3) ────────
    //
    // We are on the PLAINTEXT branch by construction — the sealed branch
    // returned above with a context principal — so a caller that reaches here
    // resolved a credential and presented no `X-TB-Ctx`. If that credential
    // names a device whose row is pinned (`e2ee_required`), it has already
    // completed a handshake once and must never be served in the clear again.
    //
    // **426 and not 401, and the difference is not cosmetic.**
    // `docs/compatibility/tb-mobile.md` maps 401 onto the re-authentication UI,
    // so answering a downgrade with 401 would send a correctly-paired phone to
    // a login screen it cannot satisfy — the credential is fine; the transport
    // is not. 426 tells it to open a context and retry, which is a thing it can
    // actually do.
    //
    // Through W1a's shared helper, never a second implementation: two copies of
    // a downgrade rule is one copy that can be forgotten. Its stated limit
    // carries over unchanged — a pinned device presenting the SHARED api key
    // resolves to `legacy` with no device row, so the pin cannot bite. That is
    // the stage-3 shared-key problem and it is R's, not this PR's.
    const downgrade = refuseUnsealedIfPinned({
      principal,
      devicesRepo: deps.devicesRepo?.(),
      context: c.get?.("e2eeContext"),
    });
    if (downgrade) {
      // The code and the status, and nothing about the request. A downgrade
      // refusal names no body, no key and no context.
      log.warn("[e2ee] pinned device refused a plaintext request", {
        event: "e2ee.rest_downgrade_refused",
      });
      return c.json(downgrade.body, downgrade.status);
    }

    // Capability check.
    const required = requiredCapability(path, method);
    if (required === null) {
      // Authenticated but unclassified. Fall through to the router rather than
      // denying: an unknown path must still 404, because answering 403 would
      // tell an authenticated caller that a route it cannot name might exist.
      //
      // This is NOT a hole — every mounted /api route is classified, and a test
      // asserts that. A genuinely new route added without a mapping reaches its
      // handler, so the fail-closed guarantee lives in that test rather than in
      // a runtime deny that would break 404s.
      await next();
      return;
    }
    if (!hasCapability(principal, required)) {
      return c.json({ error: "Forbidden", code: "MISSING_CAPABILITY", required }, 403);
    }

    c.set("principal", principal);
    await next();
  };
